// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@conklin/App/Components/Notify";
import { StyledTitleStyles } from "@conklin/App/Components/StyledTitle/StyledTitle";
import TitleTypography from "@conklin/App/Components/TitleTypography/TitleTypography";
import DataTable, { DataTableProps } from "@conklin/MobiusOverrides/DataTable/DataTable";
import DataTableBody, { DataTableBodyProps } from "@conklin/MobiusOverrides/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@conklin/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@conklin/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@conklin/MobiusOverrides/DataTable/DataTableRow";
import Link from "@conklin/MobiusOverrides/Link/Link";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@conklin/Overrides/Components/QuoteSummaryCard";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

// XNG-Changes
const enum fields {
    showQuoteNumber = "showQuoteNumber",
    showType = "showType",
    showStatus = "showStatus",
    showDateRequested = "showDateRequested",
    showDateExpires = "showDateExpires",
    showSalesRep = "showSalesRep",
    showUser = "showUser",
    showCustomer = "showCustomer",
    showShipTo = "showShipTo",
    numberOfRecords = "numberOfRecords",
}

// XNG-Changes
interface OwnProps extends WidgetProps {
    fields: {
        [fields.showQuoteNumber]: boolean;
        [fields.showType]: boolean;
        [fields.showStatus]: boolean;
        [fields.showDateRequested]: boolean;
        [fields.showDateExpires]: boolean;
        [fields.showSalesRep]: boolean;
        [fields.showUser]: boolean;
        [fields.showCustomer]: boolean;
        [fields.showShipTo]: boolean;
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Changes
        isDashboardPage: getCurrentPage(state).type === "MyAccountPage",
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    specificWidthDataTableHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    wrapper?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
};

// XNG-Changes
export const recentQuotesStyledTitleStyles: StyledTitleStyles = {};

let recentQuotesParameter = {
    page: 1,
    pageSize: 4,
    // XNG-Changes
    sort: "QuoteDate DESC",
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        recentQuotesParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        // XNG-Changes
        const cmsProperties = {
            showQuoteNumber: this.props.fields.showQuoteNumber,
            showType: this.props.fields.showType,
            showOrderTotal: this.props.fields.showOrderTotal,
            showStatus: this.props.fields.showStatus,
            showDateRequested: this.props.fields.showDateRequested,
            showDateExpires: this.props.fields.showDateExpires,
            showSalesRep: this.props.fields.showSalesRep,
            showUser: this.props.fields.showUser,
            showCustomer: this.props.fields.showCustomer,
            showShipTo: this.props.fields.showShipTo,
        };

        // XNG-Changes: strucutr
        return (
            <StyledWrapper {...styles.wrapper}>
                {this.props.isDashboardPage && (
                    <TitleTypography linkText={translate("View All Quotes")} link={myQuotesPageUrl} showIcon={true}>
                        {translate("Recent Quotes")}
                    </TitleTypography>
                )}
                {quotesDataView.value.length === 0 && (
                    <Notify
                        type="warning"
                        showClose={false}
                        fullWidth={true}
                        data-test-selector="recentQuotes_NoQuotesFound"
                    >
                        {siteMessage("Custom_No_Recent_Quotes_Message")}
                    </Notify>
                )}
                {quotesDataView.value.length > 0 && (
                    <>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                {cmsProperties.showDateRequested && (
                                    <DataTableHeader {...styles.specificWidthDataTableHeader}>
                                        {translate("Requested")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showQuoteNumber && (
                                    <DataTableHeader {...styles.specificWidthDataTableHeader}>
                                        {translate("Quote Number")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showType && settingsCollection.quoteSettings.jobQuoteEnabled && (
                                    <DataTableHeader {...styles.dataTableHeaders}>{translate("Type")}</DataTableHeader>
                                )}
                                {cmsProperties.showStatus && (
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        {translate("Status")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showSalesRep && session.isSalesPerson && (
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        {translate("Sales Rep")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showUser && session.isSalesPerson && (
                                    <DataTableHeader {...styles.dataTableHeaders}>{translate("User")}</DataTableHeader>
                                )}
                                {cmsProperties.showCustomer && (
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        {translate("Customer")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showShipTo && !session.isSalesPerson && (
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        {translate("Ship-To/Pickup")}
                                    </DataTableHeader>
                                )}
                                {cmsProperties.showDateExpires && (
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        {translate("Expires")}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {quotesDataView.value.map(
                                    quote =>
                                        quote !== null && (
                                            <DataTableRow
                                                className="product-row"
                                                data-test-selector="recentQuotes_dataLine"
                                                key={quote.id}
                                            >
                                                <QuoteSummaryCard
                                                    quote={quote}
                                                    session={session}
                                                    quoteSettings={settingsCollection.quoteSettings}
                                                    extendedStyles={styles.quoteSummaryCard}
                                                    // Xng-Changes
                                                    cmsProperties={cmsProperties}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                        {!this.props.isDashboardPage && (
                            <Link href={myQuotesPageUrl} {...styles.viewAllLink}>
                                {translate("View All Quotes")}
                            </Link>
                        )}
                    </>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.showQuoteNumber,
                displayName: "Quote #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showType,
                displayName: "Type",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showDateRequested,
                displayName: "Date Requested",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showDateExpires,
                displayName: "Date Expires",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showSalesRep,
                displayName: "Sales Req",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showUser,
                displayName: "User",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showCustomer,
                displayName: "Customer",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship-To/Pickup",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 10,
            },
        ],
    },
};

export default widgetModule;
